import { ViewBody, ViewHeader } from '@affine/core/modules/workbench';
import { resolveBlobDisplayName } from '@affine/core/utils/blob-display-name';
import type { AttachmentBlockModel } from '@blocksuite/affine/model';
import { filesize } from 'filesize';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { AttachmentFallback, AttachmentPreviewErrorBoundary } from './error';
import { PDFViewer } from './pdf/pdf-viewer';
import { TextViewer } from './text/text-viewer';
import type {
  AttachmentTextEditorState,
  AttachmentViewerBaseProps,
  AttachmentViewerInputProps,
  AttachmentViewerSource,
} from './types';
import { buildAttachmentProps } from './utils';
import { Titlebar } from './viewer';
import * as styles from './viewer.css';

function useAttachmentViewerBaseProps({
  model,
  source,
  cleanupReplacedBlob,
  onTextEditorStateChange,
}: {
  model?: AttachmentBlockModel;
  source?: AttachmentViewerSource;
  cleanupReplacedBlob?: (blobId: string) => Promise<boolean>;
  onTextEditorStateChange?: (state: AttachmentTextEditorState | null) => void;
}) {
  const [localName, setLocalName] = useState<string | null>(null);
  const [localReplacement, setLocalReplacement] = useState<{
    blobId: string;
    mimeType: string;
    size: number;
  } | null>(null);

  useEffect(() => {
    setLocalName(null);
    setLocalReplacement(null);
  }, [model?.id, source?.id]);

  const modelName = model?.props.name;
  const modelSourceId = model?.props.sourceId;
  const modelType = model?.props.type;
  const modelSize = model?.props.size;
  const props = useMemo(
    () => buildAttachmentProps(source ?? model!),
    // The model reference is stable while these observable props mutate.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [model, modelName, modelSize, modelSourceId, modelType, source]
  );
  const renamableSource = useMemo<AttachmentViewerSource>(() => {
    const viewSource: AttachmentViewerSource = {
      ...props.source,
      ...(localName ? { name: localName } : {}),
      ...(localReplacement ?? {}),
    };
    const sourceRename = props.source.rename;
    const sourceReplaceBlob = props.source.replaceBlob;

    return {
      ...viewSource,
      rename: sourceRename
        ? nextName => {
            sourceRename(nextName);
            setLocalName(
              resolveBlobDisplayName({
                blobId: props.source.blobId,
                occurrenceName: nextName,
                mimeType: props.source.mimeType,
              })
            );
          }
        : undefined,
      replaceBlob: sourceReplaceBlob
        ? async replacement => {
            await sourceReplaceBlob(replacement);
            setLocalReplacement(replacement);
          }
        : undefined,
    };
  }, [localName, localReplacement, props.source]);

  return {
    ...buildAttachmentProps(renamableSource),
    cleanupReplacedBlob,
    onTextEditorStateChange,
  };
}

// Peek view
export const AttachmentViewer = ({
  model,
  source,
  navigation,
  onClose,
  hideTitlebarClose,
  std,
  cleanupReplacedBlob,
  onTextEditorStateChange,
}: AttachmentViewerInputProps) => {
  const props = useAttachmentViewerBaseProps({
    model,
    source,
    cleanupReplacedBlob,
    onTextEditorStateChange,
  });
  const [textEditorState, setTextEditorState] =
    useState<AttachmentTextEditorState | null>(null);
  const handleTextEditorStateChange = useCallback(
    (state: AttachmentTextEditorState | null) => {
      setTextEditorState(state);
      onTextEditorStateChange?.(state);
    },
    [onTextEditorStateChange]
  );
  const displayedSize =
    textEditorState?.resolvedSize === null ||
    textEditorState?.resolvedSize === undefined
      ? props.size
      : filesize(textEditorState.resolvedSize);

  return (
    <div className={styles.viewerContainer}>
      <Titlebar
        {...props}
        size={displayedSize}
        textEditorState={textEditorState}
        onClose={onClose}
        hideCloseButton={hideTitlebarClose}
      />
      <AttachmentViewerInner
        {...props}
        navigation={navigation}
        onClose={onClose}
        std={std}
        onTextEditorStateChange={handleTextEditorStateChange}
      />
    </div>
  );
};

// Split view or standalone page
export const AttachmentViewerView = ({
  model,
  source,
  navigation,
  onClose,
  std,
  cleanupReplacedBlob,
  onTextEditorStateChange,
}: AttachmentViewerInputProps) => {
  const props = useAttachmentViewerBaseProps({
    model,
    source,
    cleanupReplacedBlob,
    onTextEditorStateChange,
  });
  const [textEditorState, setTextEditorState] =
    useState<AttachmentTextEditorState | null>(null);
  const handleTextEditorStateChange = useCallback(
    (state: AttachmentTextEditorState | null) => {
      setTextEditorState(state);
      onTextEditorStateChange?.(state);
    },
    [onTextEditorStateChange]
  );
  const displayedSize =
    textEditorState?.resolvedSize === null ||
    textEditorState?.resolvedSize === undefined
      ? props.size
      : filesize(textEditorState.resolvedSize);

  return (
    <>
      <ViewHeader>
        <Titlebar
          {...props}
          size={displayedSize}
          textEditorState={textEditorState}
          onClose={onClose}
        />
      </ViewHeader>
      <ViewBody>
        <AttachmentViewerInner
          {...props}
          navigation={navigation}
          onClose={onClose}
          std={std}
          onTextEditorStateChange={handleTextEditorStateChange}
        />
      </ViewBody>
    </>
  );
};

const AttachmentViewerInner = (props: AttachmentViewerBaseProps) => {
  const isPDF = props.source.mimeType.endsWith('pdf');
  const isText = props.source.mimeType.startsWith('text/');

  if (isPDF) {
    return (
      <AttachmentPreviewErrorBoundary>
        <PDFViewer {...props} />
      </AttachmentPreviewErrorBoundary>
    );
  }

  if (isText) {
    return (
      <AttachmentPreviewErrorBoundary>
        <TextViewer {...props} />
      </AttachmentPreviewErrorBoundary>
    );
  }

  return <AttachmentFallback {...props} />;
};
